'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';

type MobileOverlay = 'menu' | 'search' | null;

interface MobileOverlayContextValue {
  activeOverlay: MobileOverlay;
  openOverlay: (overlay: Exclude<MobileOverlay, null>) => void;
  closeOverlay: () => void;
}

const MobileOverlayContext = createContext<MobileOverlayContextValue | null>(null);

/**
 * Coordinates the mobile header's two overlays (hamburger drawer, search
 * panel) so only one is ever open — opening one closes the other instead of
 * both stacking on top of each other. Wraps the whole header in header.tsx,
 * since `MobileNavigation` and `MobileSearchTrigger` are siblings there, not
 * nested, and so need a common ancestor to share this through.
 */
export function MobileOverlayProvider({ children }: { children: React.ReactNode }) {
  const [activeOverlay, setActiveOverlay] = useState<MobileOverlay>(null);
  const pathname = usePathname();

  // An overlay belongs to the page it was opened on. Every link inside both of
  // them does call closeOverlay on its way out, but that makes closing a
  // property of each individual link rather than of navigating — so anything
  // that changes the route WITHOUT going through one of those handlers leaves
  // a full-screen panel sitting over the destination: the back and forward
  // buttons, the store switcher the drawer can be given as children, a link
  // added later that forgets the callback. Watching the route closes it once,
  // for all of them.
  //
  // Setting state that is already null is a no-op in React, so the mount pass
  // and any re-render at the same path cost nothing.
  useEffect(() => {
    setActiveOverlay(null);
  }, [pathname]);

  const value = useMemo(
    () => ({
      activeOverlay,
      openOverlay: (overlay: Exclude<MobileOverlay, null>) => setActiveOverlay(overlay),
      closeOverlay: () => setActiveOverlay(null),
    }),
    [activeOverlay],
  );

  return <MobileOverlayContext.Provider value={value}>{children}</MobileOverlayContext.Provider>;
}

export function useMobileOverlay() {
  const context = useContext(MobileOverlayContext);
  if (!context) {
    throw new Error('useMobileOverlay must be used within a MobileOverlayProvider');
  }
  return context;
}
